/** Minimal GeoJSON helpers (WGS84 lon/lat). No external dependencies. */

export type Position = [number, number];
export interface PointGeometry {
  type: "Point";
  coordinates: Position;
}
export interface LineStringGeometry {
  type: "LineString";
  coordinates: Position[];
}
export interface PolygonGeometry {
  type: "Polygon";
  coordinates: Position[][];
}
export type Geometry = PointGeometry | LineStringGeometry | PolygonGeometry;

export interface Feature<G extends Geometry = Geometry, P = Record<string, unknown>> {
  type: "Feature";
  id?: string | number;
  geometry: G;
  properties: P;
}
export interface FeatureCollection<G extends Geometry = Geometry, P = Record<string, unknown>> {
  type: "FeatureCollection";
  features: Feature<G, P>[];
}

const EARTH_RADIUS_M = 6_371_008.8;
const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** Great-circle distance in metres. */
export function haversine(a: Position, b: Position): number {
  const dLat = rad(b[1] - a[1]);
  const dLon = rad(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Length of a line in metres. */
export function lineLength(coords: Position[]): number {
  let total = 0;
  for (let i = 1; i < coords.length; i++) total += haversine(coords[i - 1]!, coords[i]!);
  return total;
}

/** Local equirectangular projection around a reference point (good to < 0.1 % at port scale). */
export function makeProjection(ref: Position) {
  const k = Math.cos(rad(ref[1]));
  const mPerDegLat = (Math.PI * EARTH_RADIUS_M) / 180;
  return {
    toXY: (p: Position): [number, number] => [(p[0] - ref[0]) * mPerDegLat * k, (p[1] - ref[1]) * mPerDegLat],
    toLonLat: (xy: [number, number]): Position => [ref[0] + xy[0] / (mPerDegLat * k), ref[1] + xy[1] / mPerDegLat],
  };
}

/** Distance along the line (m) of the point on the line closest to `p`, plus the perpendicular distance. */
export function locateOnLine(coords: Position[], p: Position): { along: number; distance: number; point: Position } {
  const proj = makeProjection(coords[0]!);
  const P = proj.toXY(p);
  let best = { along: 0, distance: Infinity, point: coords[0]! };
  let acc = 0;
  for (let i = 1; i < coords.length; i++) {
    const A = proj.toXY(coords[i - 1]!);
    const B = proj.toXY(coords[i]!);
    const dx = B[0] - A[0];
    const dy = B[1] - A[1];
    const len2 = dx * dx + dy * dy;
    const segLen = Math.sqrt(len2);
    let t = len2 === 0 ? 0 : ((P[0] - A[0]) * dx + (P[1] - A[1]) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const X: [number, number] = [A[0] + t * dx, A[1] + t * dy];
    const d = Math.hypot(P[0] - X[0], P[1] - X[1]);
    if (d < best.distance) best = { along: acc + t * segLen, distance: d, point: proj.toLonLat(X) };
    acc += segLen;
  }
  return best;
}

/** Point at `along` metres from the start of the line (clamped). */
export function pointAlong(coords: Position[], along: number): Position {
  const proj = makeProjection(coords[0]!);
  let acc = 0;
  for (let i = 1; i < coords.length; i++) {
    const A = proj.toXY(coords[i - 1]!);
    const B = proj.toXY(coords[i]!);
    const segLen = Math.hypot(B[0] - A[0], B[1] - A[1]);
    if (acc + segLen >= along || i === coords.length - 1) {
      const t = segLen === 0 ? 0 : Math.max(0, Math.min(1, (along - acc) / segLen));
      return proj.toLonLat([A[0] + t * (B[0] - A[0]), A[1] + t * (B[1] - A[1])]);
    }
    acc += segLen;
  }
  return coords[coords.length - 1]!;
}

/** Sub-line between two distances along the line. */
export function lineSlice(coords: Position[], from: number, to: number): Position[] {
  const proj = makeProjection(coords[0]!);
  const out: Position[] = [pointAlong(coords, from)];
  let acc = 0;
  for (let i = 1; i < coords.length; i++) {
    const A = proj.toXY(coords[i - 1]!);
    const B = proj.toXY(coords[i]!);
    acc += Math.hypot(B[0] - A[0], B[1] - A[1]);
    if (acc > from && acc < to) out.push(coords[i]!);
  }
  out.push(pointAlong(coords, to));
  return out;
}

/**
 * Footprint polygon of a boat moored along a quay: from `from` to `to` metres along the line,
 * extending `depth` metres to the water side (`right` = right-hand side of drawing direction).
 */
export function boatFootprint(
  coords: Position[],
  from: number,
  to: number,
  depth: number,
  waterSide: "left" | "right" = "right",
  /** metres out from the quay line where the boat starts: a boat tied alongside another lies beyond it */
  offset = 0,
): PolygonGeometry {
  const slice = lineSlice(coords, from, to);
  const proj = makeProjection(slice[0]!);
  const xy = slice.map(proj.toXY);
  const sign = waterSide === "right" ? -1 : 1;
  const out = (by: number): [number, number][] =>
    xy.map((p, i) => {
      const a = xy[Math.max(0, i - 1)]!;
      const b = xy[Math.min(xy.length - 1, i + 1)]!;
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const len = Math.hypot(dx, dy) || 1;
      return [p[0] + (sign * -dy * by) / len, p[1] + (sign * dx * by) / len];
    });
  const inner = offset ? out(offset) : xy;
  const ring = [...inner, ...out(offset + depth).reverse(), inner[0]!].map(proj.toLonLat);
  return { type: "Polygon", coordinates: [ring] };
}

/**
 * How far out from the quay line each boat of a quay lies on the map: boats on the quay start at the line, and a boat
 * alongside starts past the outer side of whatever lies between it and the quay. `depth` is how far a boat reaches out.
 */
export function waterOffsets(boats: Array<{ id: string; from: number; to: number; row: number; depth: number }>, gapM = 0.3): Map<string, number> {
  const out = new Map<string, number>();
  const done: Array<{ from: number; to: number; row: number; outer: number }> = [];
  for (const b of [...boats].sort((x, y) => x.row - y.row)) {
    const under = done.filter((d) => d.row < b.row && b.from < d.to && d.from < b.to);
    const offset = b.row === 0 ? 0 : Math.max(0, ...under.map((d) => d.outer)) + gapM;
    out.set(b.id, offset);
    done.push({ from: b.from, to: b.to, row: b.row, outer: offset + b.depth });
  }
  return out;
}

export function centroid(g: Geometry): Position {
  if (g.type === "Point") return g.coordinates;
  const pts = g.type === "LineString" ? g.coordinates : g.coordinates[0]!.slice(0, -1);
  const sum = pts.reduce<[number, number]>((s, p) => [s[0] + p[0], s[1] + p[1]], [0, 0]);
  return [sum[0] / pts.length, sum[1] / pts.length];
}

export function bbox(geoms: Geometry[]): [number, number, number, number] | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const visit = (p: Position) => {
    minX = Math.min(minX, p[0]);
    minY = Math.min(minY, p[1]);
    maxX = Math.max(maxX, p[0]);
    maxY = Math.max(maxY, p[1]);
  };
  for (const g of geoms) {
    if (g.type === "Point") visit(g.coordinates);
    else if (g.type === "LineString") g.coordinates.forEach(visit);
    else g.coordinates.forEach((r) => r.forEach(visit));
  }
  return Number.isFinite(minX) ? [minX, minY, maxX, maxY] : null;
}

/** Bearing in degrees (0 = north, clockwise) from a to b. */
export function bearing(a: Position, b: Position): number {
  const y = Math.sin(rad(b[0] - a[0])) * Math.cos(rad(b[1]));
  const x = Math.cos(rad(a[1])) * Math.sin(rad(b[1])) - Math.sin(rad(a[1])) * Math.cos(rad(b[1])) * Math.cos(rad(b[0] - a[0]));
  return (deg(Math.atan2(y, x)) + 360) % 360;
}

// ---------------------------------------------------------------------------
// Validation — returns an i18n key (and params) describing the first problem.

export interface GeometryError {
  key: string;
  params?: Record<string, string | number>;
}

function validPosition(p: unknown): p is Position {
  return (
    Array.isArray(p) &&
    p.length >= 2 &&
    typeof p[0] === "number" &&
    typeof p[1] === "number" &&
    Number.isFinite(p[0]) &&
    Number.isFinite(p[1]) &&
    p[0] >= -180 &&
    p[0] <= 180 &&
    p[1] >= -90 &&
    p[1] <= 90
  );
}

function samePos(a: Position, b: Position): boolean {
  return Math.abs(a[0] - b[0]) < 1e-12 && Math.abs(a[1] - b[1]) < 1e-12;
}

function segmentsIntersect(p1: Position, p2: Position, p3: Position, p4: Position): boolean {
  const d = (a: Position, b: Position, c: Position) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const d1 = d(p3, p4, p1);
  const d2 = d(p3, p4, p2);
  const d3 = d(p1, p2, p3);
  const d4 = d(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

function ringSelfIntersects(ring: Position[]): boolean {
  const n = ring.length - 1; // closed ring, last == first
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (Math.abs(i - j) <= 1 || (i === 0 && j === n - 1)) continue;
      if (segmentsIntersect(ring[i]!, ring[i + 1]!, ring[j]!, ring[j + 1]!)) return true;
    }
  }
  return false;
}

export function validateGeometry(g: unknown, expected?: Geometry["type"] | Geometry["type"][]): GeometryError | null {
  if (!g || typeof g !== "object") return { key: "geo.error.missing" };
  const geom = g as { type?: unknown; coordinates?: unknown };
  const allowed = expected ? (Array.isArray(expected) ? expected : [expected]) : ["Point", "LineString", "Polygon"];
  if (typeof geom.type !== "string" || !allowed.includes(geom.type as Geometry["type"])) {
    return { key: "geo.error.wrongType", params: { expected: allowed.join(" / "), got: String(geom.type) } };
  }
  const c = geom.coordinates;
  switch (geom.type) {
    case "Point":
      return validPosition(c) ? null : { key: "geo.error.badCoordinates" };
    case "LineString": {
      if (!Array.isArray(c) || c.length < 2) return { key: "geo.error.lineTooShort" };
      if (!c.every(validPosition)) return { key: "geo.error.badCoordinates" };
      const len = lineLength(c as Position[]);
      if (len < 1) return { key: "geo.error.lineZeroLength" };
      if (len > 5000) return { key: "geo.error.lineTooLong", params: { max: 5000 } };
      return null;
    }
    case "Polygon": {
      if (!Array.isArray(c) || c.length < 1) return { key: "geo.error.polygonEmpty" };
      for (const ring of c as unknown[]) {
        if (!Array.isArray(ring) || ring.length < 4) return { key: "geo.error.polygonTooFewPoints" };
        if (!ring.every(validPosition)) return { key: "geo.error.badCoordinates" };
        const r = ring as Position[];
        if (!samePos(r[0]!, r[r.length - 1]!)) return { key: "geo.error.polygonNotClosed" };
        if (ringSelfIntersects(r)) return { key: "geo.error.polygonSelfIntersects" };
        if (polygonArea(r) < 1) return { key: "geo.error.polygonZeroArea" };
      }
      return null;
    }
  }
  return { key: "geo.error.wrongType", params: { expected: allowed.join(" / "), got: String(geom.type) } };
}

/** Approximate ring area in m² (local projection, shoelace). */
export function polygonArea(ring: Position[]): number {
  const proj = makeProjection(ring[0]!);
  const xy = ring.map(proj.toXY);
  let a = 0;
  for (let i = 0; i < xy.length - 1; i++) a += xy[i]![0] * xy[i + 1]![1] - xy[i + 1]![0] * xy[i]![1];
  return Math.abs(a / 2);
}

/** Close a ring if the last point is not equal to the first. */
export function closeRing(ring: Position[]): Position[] {
  if (ring.length === 0) return ring;
  return samePos(ring[0]!, ring[ring.length - 1]!) ? ring : [...ring, ring[0]!];
}
